import React, {useCallback, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {CBCButton} from 'shared/helpers';

const COPIED_RESET_MS = 1500;

// Owns its own copied state so the label updates without the dialog being
// re-opened - useConfirmationDialog snapshots `message` into state, so JSX
// built by the caller never re-renders on its own.
const CopyLinkRow = ({url}: {url: string}) => {
    const [copied, setCopied] = useState(false);
    const resetTimer = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        return () => clearTimeout(resetTimer.current);
    }, []);

    const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
        e.target.select();
    }, []);

    const handleCopy = useCallback(() => {
        // navigator.clipboard is undefined outside secure contexts and in some
        // in-app webviews, where the property access itself would throw.
        const copy = navigator.clipboard
            ? navigator.clipboard.writeText(url)
            : Promise.reject(new Error('Clipboard unavailable'));

        copy.then(() => {
            setCopied(true);

            clearTimeout(resetTimer.current);
            resetTimer.current = setTimeout(
                () => setCopied(false),
                COPIED_RESET_MS
            );
        }).catch(() => {
            setCopied(false);
        });
    }, [url]);

    return (
        <Row>
            <UrlInput value={url} readOnly onFocus={handleFocus} />
            <CBCButton
                iconName="Options-Copy-white.svg"
                className="button-blue"
                onClick={handleCopy}
                type="button">
                {copied ? 'Copied!' : 'Copy'}
            </CBCButton>
        </Row>
    );
};

export default CopyLinkRow;

const Row = styled.div`
    display: flex;
    gap: 6px;
`;

const UrlInput = styled.input`
    flex: 1;
    min-width: 0;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 6px 8px;
    font-size: 12px;
    color: #555;
    background: #f7f7f7;

    &:focus {
        outline: none;
        border-color: #bbb;
    }
`;
